/**
 * Universal Ink & Toner Finder
 * Works across all brands using catalog JSON data
 */

(function() {
  'use strict';

  let allBrands = [];
  let currentBrandData = null;

  // ============================================
  // LOAD CATALOG INDEX
  // ============================================

  async function loadCatalogIndex() {
    try {
      const catalogUrl = window.universalFinderCatalogUrl || '/assets/catalog-index.json';
      const cacheBuster = `?v=${Date.now()}`;
      
      console.log('📦 Loading catalog index...');
      const response = await fetch(catalogUrl + cacheBuster, { cache: 'no-cache' });
      
      if (!response.ok) {
        throw new Error(`Failed to load catalog: ${response.status}`);
      }
      
      const data = await response.json();
      return data.brands || [];
    } catch (error) {
      console.error('❌ Error loading catalog index:', error);
      return [];
    }
  }

  // ============================================
  // LOAD BRAND-SPECIFIC CATALOG
  // ============================================

  async function loadBrandCatalog(brandSlug) {
    try {
      const filename = `catalog-${brandSlug}.json`;
      
      // Get the base URL from catalog-index or default to /assets/
      let baseUrl = '/assets/';
      if (window.universalFinderCatalogUrl) {
        const indexUrl = window.universalFinderCatalogUrl;
        // Extract everything before the filename
        baseUrl = indexUrl.substring(0, indexUrl.lastIndexOf('/') + 1);
      }
      
      const url = `${baseUrl}${filename}?v=${Date.now()}`;
      
      console.log(`📦 Loading ${brandSlug} catalog from: ${url}`);
      const response = await fetch(url, { cache: 'no-cache' });
      
      if (!response.ok) {
        console.error(`❌ Failed to load: ${url} - Status: ${response.status}`);
        throw new Error(`Failed to load brand catalog: ${response.status}`);
      }
      
      console.log(`✅ Loaded ${brandSlug} catalog successfully`);
      return await response.json();
    } catch (error) {
      console.error(`❌ Error loading brand catalog:`, error);
      return null;
    }
  }

  // ============================================
  // POPULATE DROPDOWNS
  // ============================================

  function populateBrandDropdown(brands, selectElement) {
    selectElement.innerHTML = '<option value="">Select a brand...</option>';
    
    brands
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(brand => {
        const option = document.createElement('option');
        option.value = brand.handle; // Use handle directly from JSON
        option.textContent = brand.name;
        option.dataset.slug = brand.handle; // Store handle for later use
        selectElement.appendChild(option);
      });
    
    selectElement.disabled = false;
  }

  function populateFamilyDropdown(families, selectElement) {
    selectElement.innerHTML = '<option value="">Select a family/series...</option>';
    
    families
      .sort((a, b) => a.title.localeCompare(b.title))
      .forEach(family => {
        const option = document.createElement('option');
        option.value = family.handle;
        option.textContent = `${family.title} (${family.models.length} models)`;
        option.dataset.familyKey = family.name; // Store the family key for model lookup
        selectElement.appendChild(option);
      });
    
    selectElement.disabled = false;
  }

  function populateModelDropdown(models, selectElement) {
    selectElement.innerHTML = '<option value="">Select a model...</option>';
    
    models
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(model => {
        const option = document.createElement('option');
        option.value = model.handle;
        option.textContent = model.name;
        selectElement.appendChild(option);
      });
    
    selectElement.disabled = false;
  }

  // ============================================
  // INITIALIZE FINDER
  // ============================================

  async function initUniversalFinder(sectionElement) {
    // Get unique ID - try finderId first (for tiles), then sectionId (for standalone sections)
    const finderId = sectionElement.dataset.finderId || sectionElement.dataset.sectionId;
    
    if (!finderId) {
      console.warn('⚠️ Finder missing ID');
      return;
    }
    
    const brandSelect = document.getElementById(`brand-select-${finderId}`);
    const familySelect = document.getElementById(`family-select-${finderId}`);
    const modelSelect = document.getElementById(`model-select-${finderId}`);
    const submitButton = document.getElementById(`finder-submit-${finderId}`);
    const loadingIndicator = document.getElementById(`finder-loading-${finderId}`);
    
    if (!brandSelect || !familySelect || !modelSelect || !submitButton) {
      console.warn('⚠️ Finder elements not found');
      return;
    }

    // Show loading
    if (loadingIndicator) {
      loadingIndicator.style.display = 'flex';
    }

    // Load all brands
    allBrands = await loadCatalogIndex();
    
    // Hide loading
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }

    if (allBrands.length === 0) {
      brandSelect.innerHTML = '<option value="">Error loading brands</option>';
      return;
    }

    // Populate brand dropdown
    populateBrandDropdown(allBrands, brandSelect);
    console.log(`✅ Loaded ${allBrands.length} brands`);

    // ============================================
    // EVENT LISTENERS
    // ============================================

    // Brand change event
    brandSelect.addEventListener('change', async function() {
      const selectedBrandSlug = this.value;
      
      // Reset downstream dropdowns
      familySelect.innerHTML = '<option value="">Loading families...</option>';
      familySelect.disabled = true;
      modelSelect.innerHTML = '<option value="">Select family first...</option>';
      modelSelect.disabled = true;
      submitButton.disabled = true;
      
      if (!selectedBrandSlug) {
        familySelect.innerHTML = '<option value="">Select brand first...</option>';
        return;
      }

      // Load brand catalog
      currentBrandData = await loadBrandCatalog(selectedBrandSlug);
      
      if (!currentBrandData || !currentBrandData.families) {
        familySelect.innerHTML = '<option value="">Error loading families</option>';
        return;
      }

      // Families can be array (from build script) or object (legacy)
      const familiesArray = Array.isArray(currentBrandData.families)
        ? currentBrandData.families
        : Object.values(currentBrandData.families);
      
      if (familiesArray.length === 0) {
        familySelect.innerHTML = '<option value="">No families found</option>';
        return;
      }

      populateFamilyDropdown(familiesArray, familySelect);
      console.log(`✅ Loaded ${familiesArray.length} families`);
    });

    // Family change event
    familySelect.addEventListener('change', function() {
      const selectedFamilyHandle = this.value;
      const selectedOption = this.options[this.selectedIndex];
      const familyKey = selectedOption?.dataset.familyKey;
      
      // Reset model dropdown
      modelSelect.innerHTML = '<option value="">Select a model...</option>';
      modelSelect.disabled = true;
      submitButton.disabled = true;
      
      if (!selectedFamilyHandle || !familyKey || !currentBrandData) {
        return;
      }

      // Families can be array or object – find by name or handle
      const familiesArray = Array.isArray(currentBrandData.families)
        ? currentBrandData.families
        : Object.values(currentBrandData.families);
      const family = familiesArray.find(
        f => (f.name && f.name === familyKey) || (f.handle && f.handle === selectedFamilyHandle)
      );
      
      if (!family || !family.models || family.models.length === 0) {
        modelSelect.innerHTML = '<option value="">No models found</option>';
        return;
      }

      populateModelDropdown(family.models, modelSelect);
      console.log(`✅ Loaded ${family.models.length} models`);
    });

    // Model change event
    modelSelect.addEventListener('change', function() {
      const selectedModelHandle = this.value;
      submitButton.disabled = !selectedModelHandle;
    });

    // Submit button click
    submitButton.addEventListener('click', function() {
      const selectedModelHandle = modelSelect.value;
      
      if (selectedModelHandle) {
        const collectionUrl = `/collections/${selectedModelHandle}`;
        console.log(`🔍 Navigating to: ${collectionUrl}`);
        window.location.href = collectionUrl;
      }
    });

    // Allow Enter key in dropdowns to submit
    [brandSelect, familySelect, modelSelect].forEach(select => {
      select.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' && !submitButton.disabled) {
          e.preventDefault();
          submitButton.click();
        }
      });
    });
  }

  // ============================================
  // AUTO-INITIALIZE ON PAGE LOAD
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Support both standalone sections and tiles
    const finderSections = document.querySelectorAll('.universal-finder, .universal-finder-tile');
    finderSections.forEach(section => {
      initUniversalFinder(section);
    });
  }

})();
